/** 기억해 두는 시간 — 어드민에서 바꾼 공개 상태가 이 시간 안에 화면에 반영된다 */
export const FEATURE_STATUS_TTL_MS = 5 * 60 * 1000;

interface Entry<T> {
    viewerKey: string;
    value: T;
    fetchedAt: number;
}

export interface FeatureStatusStore<T> {
    /** 이 사람의 값(오래됐어도) — 한 번도 받지 못했으면 null */
    peek: (viewerKey: string) => T | null;
    /** TTL 이 지났거나(또는 force) 다른 사람의 값이면 다시 조회한다 */
    load: (viewerKey: string, force?: boolean) => Promise<void>;
    /** 화면이 알아낸 값을 바로 넣는다(예: 질문을 열려다 꺼진 것을 알았을 때) */
    set: (viewerKey: string, value: T) => void;
    subscribe: (listener: () => void) => () => void;
}

/**
 * @param fetcher 서버 조회
 * @param fallback 조회에 실패했고 이 사람의 이전 값도 없을 때 쓸 값(안전하게 "꺼짐")
 */
export function createFeatureStatusStore<T>(fetcher: () => Promise<T>, fallback: T): FeatureStatusStore<T> {
    let entry: Entry<T> | null = null;
    let inFlight: { viewerKey: string; promise: Promise<void> } | null = null;
    let generation = 0;
    const listeners = new Set<() => void>();
    const notify = (): void => listeners.forEach((listener) => listener());

    const fresh = (viewerKey: string): boolean =>
        entry !== null && entry.viewerKey === viewerKey && Date.now() - entry.fetchedAt < FEATURE_STATUS_TTL_MS;

    const load = (viewerKey: string, force = false): Promise<void> => {
        if (!force && fresh(viewerKey)) {
            // 이전 사용자 요청 중 캐시된 사용자로 돌아온 경우도 늦은 응답을 무효화한다.
            if (inFlight && inFlight.viewerKey !== viewerKey) {
                ++generation;
                inFlight = null;
            }
            return Promise.resolve();
        }
        if (inFlight && inFlight.viewerKey === viewerKey) return inFlight.promise;
        const requestGeneration = ++generation;
        const promise: Promise<void> = fetcher()
            .then((value) => {
                if (requestGeneration !== generation) return;
                entry = { viewerKey, value, fetchedAt: Date.now() };
            })
            .catch(() => {
                if (requestGeneration !== generation) return;
                // 실패 — 같은 사람의 이전 값이 있으면 그대로 두고, 없으면 꺼진 것으로. 다음 화면에서 바로 다시 시도하게 시각은 0
                entry = { viewerKey, value: entry?.viewerKey === viewerKey ? entry.value : fallback, fetchedAt: 0 };
            })
            .finally(() => {
                if (inFlight?.promise === promise) inFlight = null;
                if (requestGeneration === generation) notify();
            });
        inFlight = { viewerKey, promise };
        return promise;
    };

    return {
        peek: (viewerKey) => (entry?.viewerKey === viewerKey ? entry.value : null),
        load,
        set: (viewerKey, value) => {
            ++generation;
            inFlight = null;
            entry = { viewerKey, value, fetchedAt: Date.now() };
            notify();
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}

